// Pricing4.js - Contains functions for creating a modern pricing section with cards
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Pricing section with three-tier pricing cards similar to the provided image
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the pricing section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The pricing section elements
 */
export const createPricingElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const pricingSectionClass = 'pricing4-section';
  const pricingContainerClass = 'pricing4-container';
  const pricingCardsClass = 'pricing4-cards';
  const pricingCardClass = 'pricing4-card';
  const pricingCardStandardClass = 'pricing4-card-standard';
  const pricingCardProClass = 'pricing4-card-pro';
  const pricingCardPremiumClass = 'pricing4-card-premium';
  const pricingLabelClass = 'pricing4-label';
  const pricingLabelProClass = 'pricing4-label-pro';
  const pricingTitleClass = 'pricing4-title';
  const pricingTitleProClass = 'pricing4-title-pro';
  const pricingPriceClass = 'pricing4-price';
  const pricingOriginalPriceClass = 'pricing4-original-price';
  const pricingOriginalPriceProClass = 'pricing4-original-price-pro';
  const pricingDescriptionClass = 'pricing4-description';
  const pricingDescriptionProClass = 'pricing4-description-pro';
  const pricingFeaturesClass = 'pricing4-features';
  const pricingFeatureClass = 'pricing4-feature';
  const pricingFeatureProClass = 'pricing4-feature-pro';
  const pricingFeatureIconClass = 'pricing4-feature-icon';
  const pricingButtonClass = 'pricing4-button';
  const pricingButtonStandardClass = 'pricing4-button-standard';
  const pricingButtonProClass = 'pricing4-button-pro';
  
  // Define styles
  // Desktop Styles (default)
  const pricingSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const pricingContainerStyles = {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  const pricingCardsStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '30px',
    width: '100%',
    position: 'relative'
  };

  const pricingCardStyles = {
    display: 'flex',
    flexDirection: 'column',
    padding: '50px 30px',
    borderRadius: '15px',
    width: '100%',
    maxWidth: '380px',
    backgroundColor: '#ffffff',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    border: '1px solid #f0f0f0'
  };

  const pricingCardStandardStyles = {
    backgroundColor: '#ffffff'
  };

  const pricingCardProStyles = {
    background: 'linear-gradient(135deg, #7a5cf0 0%, #4a35a0 100%)',
    color: '#ffffff',
    boxShadow: '0px 10px 30px rgba(122, 92, 240, 0.3)',
    transform: 'scale(1.08)',
    zIndex: '2',
    padding: '60px 35px',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const pricingCardPremiumStyles = {
    backgroundColor: '#ffffff',
    border: '1px solid #f0f0f0'
  };

  const pricingLabelStyles = {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  };

  const pricingLabelProStyles = {
    color: 'rgba(255, 255, 255, 0.95)',
    letterSpacing: '1.5px',
    fontWeight: '700'
  };

  const pricingTitleStyles = {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '5px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const pricingTitleProStyles = {
    color: '#ffffff',
    fontWeight: '800',
    textShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
  };

  const pricingPriceStyles = {
    fontSize: '24px',
    fontWeight: '400',
    marginBottom: '20px'
  };

  const pricingOriginalPriceStyles = {
    fontSize: '20px',
    fontWeight: '400',
    textDecoration: 'line-through',
    opacity: '0.7',
    display: 'inline-block'
  };

  const pricingOriginalPriceProStyles = {
    color: 'rgba(255, 255, 255, 0.7)'
  };

  const pricingDescriptionStyles = {
    fontSize: '16px',
    lineHeight: '1.6',
    marginBottom: '30px',
    opacity: '0.9'
  };

  const pricingDescriptionProStyles = {
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: '1.7'
  };

  const pricingFeaturesStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginBottom: '40px'
  };

  const pricingFeatureStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    fontWeight: '400'
  };

  const pricingFeatureProStyles = {
    color: 'rgba(255, 255, 255, 0.95)'
  };

  const pricingFeatureIconStyles = {
    width: '20px',
    height: '20px',
    minWidth: '20px'
  };

  const pricingButtonStyles = {
    padding: '16px 24px',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    textAlign: 'center',
    marginTop: 'auto',
    transition: 'all 0.3s ease',
    width: '100%',
    backgroundColor: '#f5f5f5',
    border: 'none'
  };

  const pricingButtonStandardStyles = {
    backgroundColor: '#f0f0f0',
    color: '#333333',
    border: 'none',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)',
    fontWeight: '600'
  };

  const pricingButtonProStyles = {
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
    color: '#5b42b5',
    border: 'none',
    boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.15)',
    fontWeight: '700'
  };

  // Tablet Styles
  const tabletPricingCardsStyles = {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '15px'
  };

  const tabletPricingCardStyles = {
    maxWidth: '320px'
  };

  const tabletPricingCardProStyles = {
    transform: 'scale(1.03)'
  };

  // Mobile Styles
  const mobilePricingCardsStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '30px',
    padding: '0 15px'
  };

  const mobilePricingCardStyles = {
    maxWidth: '100%',
    width: '100%'
  };

  const mobilePricingCardProStyles = {
    transform: 'scale(1.03)',
    order: '-1',
    padding: '50px 30px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(pricingSectionClass, pricingSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingContainerClass, pricingContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardsClass, pricingCardsStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardClass, pricingCardStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardStandardClass, pricingCardStandardStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardProClass, pricingCardProStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardPremiumClass, pricingCardPremiumStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingLabelClass, pricingLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingLabelProClass, pricingLabelProStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingTitleClass, pricingTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingTitleProClass, pricingTitleProStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingPriceClass, pricingPriceStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingOriginalPriceClass, pricingOriginalPriceStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingOriginalPriceProClass, pricingOriginalPriceProStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingDescriptionClass, pricingDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingDescriptionProClass, pricingDescriptionProStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeaturesClass, pricingFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeatureClass, pricingFeatureStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeatureProClass, pricingFeatureProStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeatureIconClass, pricingFeatureIconStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingButtonClass, pricingButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingButtonStandardClass, pricingButtonStandardStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingButtonProClass, pricingButtonProStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(pricingCardsClass, tabletPricingCardsStyles);
    newRegistry[DEVICES.TABLET].set(pricingCardClass, tabletPricingCardStyles);
    newRegistry[DEVICES.TABLET].set(pricingCardProClass, tabletPricingCardProStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(pricingCardsClass, mobilePricingCardsStyles);
    newRegistry[DEVICES.MOBILE].set(pricingCardClass, mobilePricingCardStyles);
    newRegistry[DEVICES.MOBILE].set(pricingCardProClass, mobilePricingCardProStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    pricingSectionClass,
    pricingContainerClass,
    pricingCardsClass,
    pricingCardClass,
    pricingCardStandardClass,
    pricingCardProClass,
    pricingCardPremiumClass,
    pricingLabelClass,
    pricingLabelProClass,
    pricingTitleClass,
    pricingTitleProClass,
    pricingPriceClass,
    pricingOriginalPriceClass,
    pricingOriginalPriceProClass,
    pricingDescriptionClass,
    pricingDescriptionProClass,
    pricingFeaturesClass,
    pricingFeatureClass,
    pricingFeatureProClass,
    pricingFeatureIconClass,
    pricingButtonClass,
    pricingButtonStandardClass,
    pricingButtonProClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  const cardsContainerId = Date.now() + 2;
  
  // Standard card elements
  const standardCardId = Date.now() + 3;
  const standardLabelId = Date.now() + 4;
  const standardTitleId = Date.now() + 5;
  const standardPriceId = Date.now() + 6;
  const standardDescriptionId = Date.now() + 7;
  const standardFeaturesId = Date.now() + 8;
  const standardFeature1Id = Date.now() + 9;
  const standardFeature2Id = Date.now() + 10;
  const standardFeature3Id = Date.now() + 11;
  const standardFeature4Id = Date.now() + 12;
  const standardButtonId = Date.now() + 13;
  
  // Pro card elements
  const proCardId = Date.now() + 14;
  const proLabelId = Date.now() + 15;
  const proTitleId = Date.now() + 16;
  const proPriceId = Date.now() + 17;
  const proOriginalPriceId = Date.now() + 18;
  const proDescriptionId = Date.now() + 19;
  const proFeaturesId = Date.now() + 20;
  const proFeature1Id = Date.now() + 21;
  const proFeature2Id = Date.now() + 22;
  const proFeature3Id = Date.now() + 23;
  const proFeature4Id = Date.now() + 24;
  const proFeature5Id = Date.now() + 25;
  const proButtonId = Date.now() + 26;
  
  // Premium card elements
  const premiumCardId = Date.now() + 27;
  const premiumLabelId = Date.now() + 28;
  const premiumTitleId = Date.now() + 29;
  const premiumPriceId = Date.now() + 30;
  const premiumOriginalPriceId = Date.now() + 31;
  const premiumDescriptionId = Date.now() + 32;
  const premiumFeaturesId = Date.now() + 33;
  const premiumFeature1Id = Date.now() + 34;
  const premiumFeature2Id = Date.now() + 35;
  const premiumFeature3Id = Date.now() + 36;
  const premiumFeature4Id = Date.now() + 37;
  const premiumFeature5Id = Date.now() + 38;
  const premiumFeature6Id = Date.now() + 39;
  const premiumButtonId = Date.now() + 40;
  
  // Feature icons
  const standardIcon1Id = Date.now() + 41;
  const standardIcon2Id = Date.now() + 42;
  const standardIcon3Id = Date.now() + 43;
  const standardIcon4Id = Date.now() + 44;
  
  const proIcon1Id = Date.now() + 45;
  const proIcon2Id = Date.now() + 46;
  const proIcon3Id = Date.now() + 47;
  const proIcon4Id = Date.now() + 48;
  const proIcon5Id = Date.now() + 49;
  
  const premiumIcon1Id = Date.now() + 50;
  const premiumIcon2Id = Date.now() + 51;
  const premiumIcon3Id = Date.now() + 52;
  const premiumIcon4Id = Date.now() + 53;
  const premiumIcon5Id = Date.now() + 54;
  const premiumIcon6Id = Date.now() + 55;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Pricing Section',
      parentId: null,
      className: pricingSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'pricing-section',
        'data-component': 'section',
        'data-section-type': 'pricing',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: pricingContainerClass,
      styles: {},
      children: [cardsContainerId],
      text: '',
      attributes: {
        'data-element': 'pricing-container'
      }
    },
    
    // Cards container
    {
      id: cardsContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Cards',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardsClass,
      styles: {},
      children: [standardCardId, proCardId, premiumCardId],
      text: '',
      attributes: {
        'data-element': 'pricing-cards'
      }
    },
    
    // Standard Card
    {
      id: standardCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Standard Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingCardClass} ${pricingCardStandardClass}`,
      styles: {},
      children: [standardLabelId, standardTitleId, standardPriceId, standardDescriptionId, standardFeaturesId, standardButtonId],
      text: '',
      attributes: {
        'data-element': 'pricing-card',
        'data-plan': 'standard'
      }
    },
    
    // Standard Label
    {
      id: standardLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Standard Label',
      parentId: standardCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingLabelClass,
      styles: {
        color: '#333333'
      },
      children: [],
      text: 'STANDARD LOGO DESIGN',
      attributes: {
        'data-element': 'pricing-label'
      }
    },
    
    // Standard Title
    {
      id: standardTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Standard Title',
      parentId: standardCardId,
      hierarchyName: getHierarchyName('h3'),
      className: pricingTitleClass,
      styles: {
        color: '#333333'
      },
      children: [],
      text: '$500',
      attributes: {
        'data-element': 'pricing-title'
      }
    },
    
    // Standard Price
    {
      id: standardPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Standard Description',
      parentId: standardCardId,
      hierarchyName: getHierarchyName('p'),
      className: pricingDescriptionClass,
      styles: {
        color: '#666666'
      },
      children: [],
      text: 'I design the logo that reflects the identity of your brand in a professional and unique way.',
      attributes: {
        'data-element': 'pricing-description'
      }
    },
    
    // Standard Features
    {
      id: standardFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Standard Features',
      parentId: standardCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeaturesClass,
      styles: {},
      children: [standardFeature1Id, standardFeature2Id, standardFeature3Id, standardFeature4Id],
      text: '',
      attributes: {
        'data-element': 'pricing-features'
      }
    },
    
    // Standard Feature 1
    {
      id: standardFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Standard Feature 1',
      parentId: standardFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [standardIcon1Id],
      text: '1 logo variation',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Standard Feature 1 Icon
    {
      id: standardIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Standard Feature 1 Icon',
      parentId: standardFeature1Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Standard Feature 2
    {
      id: standardFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Standard Feature 2',
      parentId: standardFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [standardIcon2Id],
      text: '3 revisions',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Standard Feature 2 Icon
    {
      id: standardIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Standard Feature 2 Icon',
      parentId: standardFeature2Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Standard Feature 3
    {
      id: standardFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Standard Feature 3',
      parentId: standardFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [standardIcon3Id],
      text: 'AI + JPG, PNG delivery',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Standard Feature 3 Icon
    {
      id: standardIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Standard Feature 3 Icon',
      parentId: standardFeature3Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Standard Feature 4
    {
      id: standardFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Standard Feature 4',
      parentId: standardFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [standardIcon4Id],
      text: 'Est. 5 days of delivery',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Standard Feature 4 Icon
    {
      id: standardIcon4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Standard Feature 4 Icon',
      parentId: standardFeature4Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Standard Button
    {
      id: standardButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Standard Button',
      parentId: standardCardId,
      hierarchyName: getHierarchyName('button'),
      className: `${pricingButtonClass} ${pricingButtonStandardClass}`,
      styles: {},
      children: [],
      text: 'Click here to get started',
      attributes: {
        'data-element': 'pricing-button'
      }
    },
    
    // Pro card elements
    {
      id: proCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingCardClass} ${pricingCardProClass}`,
      styles: {},
      children: [proLabelId, proTitleId, proPriceId, proFeaturesId, proButtonId],
      text: '',
      attributes: {
        'data-element': 'pricing-card',
        'data-plan': 'pro'
      }
    },
    
    // Pro Label with improved styling
    {
      id: proLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Label',
      parentId: proCardId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingLabelClass} ${pricingLabelProClass}`,
      styles: {},
      children: [],
      text: 'PRO LOGO DESIGN',
      attributes: {
        'data-element': 'pricing-label'
      }
    },
    
    // Pro Title with improved styling
    {
      id: proTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Pro Title',
      parentId: proCardId,
      hierarchyName: getHierarchyName('h3'),
      className: `${pricingTitleClass} ${pricingTitleProClass}`,
      styles: {},
      children: [proOriginalPriceId],
      text: '$1,000',
      attributes: {
        'data-element': 'pricing-title'
      }
    },
    
    // Pro Original Price with improved styling
    {
      id: proOriginalPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Pro Original Price',
      parentId: proTitleId,
      hierarchyName: getHierarchyName('span'),
      className: `${pricingOriginalPriceClass} ${pricingOriginalPriceProClass}`,
      styles: {},
      children: [],
      text: '$1,500',
      attributes: {
        'data-element': 'pricing-original-price'
      }
    },
    
    // Pro Description with improved styling
    {
      id: proPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Pro Description',
      parentId: proCardId,
      hierarchyName: getHierarchyName('p'),
      className: `${pricingDescriptionClass} ${pricingDescriptionProClass}`,
      styles: {},
      children: [],
      text: 'I design the logo that reflects the identity of your brand in a professional and unique way.',
      attributes: {
        'data-element': 'pricing-description'
      }
    },
    
    // Pro Features
    {
      id: proFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Features',
      parentId: proCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeaturesClass,
      styles: {},
      children: [proFeature1Id, proFeature2Id, proFeature3Id, proFeature4Id, proFeature5Id],
      text: '',
      attributes: {
        'data-element': 'pricing-features'
      }
    },
    
    // Pro Feature 1
    {
      id: proFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 1',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingFeatureClass} ${pricingFeatureProClass}`,
      styles: {},
      children: [proIcon1Id],
      text: '2 logo variations',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Feature 1 Icon
    {
      id: proIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 1 Icon',
      parentId: proFeature1Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%23ffffff',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Feature 2
    {
      id: proFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 2',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingFeatureClass} ${pricingFeatureProClass}`,
      styles: {},
      children: [proIcon2Id],
      text: '4 revisions',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Feature 2 Icon
    {
      id: proIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 2 Icon',
      parentId: proFeature2Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%23ffffff',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Feature 3
    {
      id: proFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 3',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingFeatureClass} ${pricingFeatureProClass}`,
      styles: {},
      children: [proIcon3Id],
      text: 'AI + JPG, PNG delivery',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Feature 3 Icon
    {
      id: proIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 3 Icon',
      parentId: proFeature3Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%23ffffff',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Feature 4
    {
      id: proFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 4',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingFeatureClass} ${pricingFeatureProClass}`,
      styles: {},
      children: [proIcon4Id],
      text: 'Est. 3 days of delivery',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Feature 4 Icon
    {
      id: proIcon4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 4 Icon',
      parentId: proFeature4Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%23ffffff',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Feature 5
    {
      id: proFeature5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 5',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingFeatureClass} ${pricingFeatureProClass}`,
      styles: {},
      children: [proIcon5Id],
      text: 'Unlimited revisions',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Feature 5 Icon
    {
      id: proIcon5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 5 Icon',
      parentId: proFeature5Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%23ffffff',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Button with improved styling
    {
      id: proButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Pro Button',
      parentId: proCardId,
      hierarchyName: getHierarchyName('button'),
      className: `${pricingButtonClass} ${pricingButtonProClass}`,
      styles: {},
      children: [],
      text: 'Click here to get started',
      attributes: {
        'data-element': 'pricing-button'
      }
    },
    
    // Premium card elements
    {
      id: premiumCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Premium Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingCardClass} ${pricingCardPremiumClass}`,
      styles: {},
      children: [premiumLabelId, premiumTitleId, premiumPriceId, premiumFeaturesId, premiumButtonId],
      text: '',
      attributes: {
        'data-element': 'pricing-card',
        'data-plan': 'premium'
      }
    },
    
    // Premium Label
    {
      id: premiumLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Premium Label',
      parentId: premiumCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingLabelClass,
      styles: {
        color: '#333333'
      },
      children: [],
      text: 'PREMIUM LOGO DESIGN',
      attributes: {
        'data-element': 'pricing-label'
      }
    },
    
    // Premium Title with original price
    {
      id: premiumTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Premium Title',
      parentId: premiumCardId,
      hierarchyName: getHierarchyName('h3'),
      className: pricingTitleClass,
      styles: {
        color: '#333333'
      },
      children: [premiumOriginalPriceId],
      text: '$1,500',
      attributes: {
        'data-element': 'pricing-title'
      }
    },
    
    // Premium Original Price
    {
      id: premiumOriginalPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Premium Original Price',
      parentId: premiumTitleId,
      hierarchyName: getHierarchyName('span'),
      className: pricingOriginalPriceClass,
      styles: {
        color: '#666666',
        opacity: '0.7'
      },
      children: [],
      text: '$2,000',
      attributes: {
        'data-element': 'pricing-original-price'
      }
    },
    
    // Premium Price
    {
      id: premiumPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Premium Description',
      parentId: premiumCardId,
      hierarchyName: getHierarchyName('p'),
      className: pricingDescriptionClass,
      styles: {
        color: '#666666'
      },
      children: [],
      text: 'I design the logo that reflects the identity of your brand in a professional and unique way.',
      attributes: {
        'data-element': 'pricing-description'
      }
    },
    
    // Premium Description
    {
      id: premiumDescriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Premium Description',
      parentId: premiumCardId,
      hierarchyName: getHierarchyName('p'),
      className: pricingDescriptionClass,
      styles: {
        color: '#ffffff'
      },
      children: [],
      text: 'I design the logo that reflects the identity of your brand in a professional and unique way.',
      attributes: {
        'data-element': 'pricing-description'
      }
    },
    
    // Premium Features
    {
      id: premiumFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Premium Features',
      parentId: premiumCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeaturesClass,
      styles: {},
      children: [premiumFeature1Id, premiumFeature2Id, premiumFeature3Id, premiumFeature4Id, premiumFeature5Id, premiumFeature6Id],
      text: '',
      attributes: {
        'data-element': 'pricing-features'
      }
    },
    
    // Premium Feature 1
    {
      id: premiumFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Premium Feature 1',
      parentId: premiumFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [premiumIcon1Id],
      text: '3 logo variations',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Premium Feature 1 Icon
    {
      id: premiumIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Premium Feature 1 Icon',
      parentId: premiumFeature1Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Premium Feature 2
    {
      id: premiumFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Premium Feature 2',
      parentId: premiumFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [premiumIcon2Id],
      text: '6 revisions',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Premium Feature 2 Icon
    {
      id: premiumIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Premium Feature 2 Icon',
      parentId: premiumFeature2Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Premium Feature 3
    {
      id: premiumFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Premium Feature 3',
      parentId: premiumFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [premiumIcon3Id],
      text: 'AI + JPG, PNG delivery',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Premium Feature 3 Icon
    {
      id: premiumIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Premium Feature 3 Icon',
      parentId: premiumFeature3Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Premium Feature 4
    {
      id: premiumFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Premium Feature 4',
      parentId: premiumFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [premiumIcon4Id],
      text: 'Est. 3 days of delivery',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Premium Feature 4 Icon
    {
      id: premiumIcon4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Premium Feature 4 Icon',
      parentId: premiumFeature4Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Premium Feature 5
    {
      id: premiumFeature5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Premium Feature 5',
      parentId: premiumFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [premiumIcon5Id],
      text: 'Unlimited revisions',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Premium Feature 5 Icon
    {
      id: premiumIcon5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Premium Feature 5 Icon',
      parentId: premiumFeature5Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Premium Feature 6
    {
      id: premiumFeature6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Premium Feature 6',
      parentId: premiumFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {
        color: '#666666'
      },
      children: [premiumIcon6Id],
      text: 'AI + JPG, PNG delivery',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Premium Feature 6 Icon
    {
      id: premiumIcon6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Premium Feature 6 Icon',
      parentId: premiumFeature6Id,
      hierarchyName: getHierarchyName('image'),
      className: pricingFeatureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle.svg?color=%237a5cf0',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Premium Button
    {
      id: premiumButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Premium Button',
      parentId: premiumCardId,
      hierarchyName: getHierarchyName('button'),
      className: `${pricingButtonClass} ${pricingButtonStandardClass}`,
      styles: {},
      children: [],
      text: 'Click here to get started',
      attributes: {
        'data-element': 'pricing-button'
      }
    }
  ];

  return {
    pricingSection: newElements[0],
    elements: newElements
  };
};

export default createPricingElements; 